/**
 * Short-lived in-memory cache for the dropdown/master lists the order page needs
 * (parties, designs, colors, production units, transporters, brokers, vendor parties).
 *
 * Why: these lists rarely change, but the order page used to download all ten of them
 * every time an order was opened. Within LOOKUP_CACHE_TTL_MS the lists are reused.
 *
 * Safety rules (see OrderDetail.js):
 *  - The order page always loads fresh lists for new orders, drafts (the only editable
 *    orders) and when the "Send to vendor" dialog is opened.
 *  - The cache belongs to the logged-in session token, so another user logging in on the
 *    same browser never sees lists loaded for someone else.
 *  - A load where any list failed (network / server error) is not cached.
 *  - Nothing is stored on disk; a page refresh starts empty.
 */

export const LOOKUP_CACHE_TTL_MS = 5 * 60 * 1000;

const LOOKUP_REQUESTS = {
  parties: '/api/parties?category=PARTIES',
  designs: '/api/designs',
  colorOptions: '/api/insights?type=COLOR',
  khatas: '/api/khatas',
  transportParties: '/api/parties?category=TRANSPORT_PARTIES',
  brokers: '/api/brokers',
  buttaCutting: '/api/parties?category=BUTTA_CUTTING_PARTIES',
  millFinish: '/api/parties?category=MILL_FINISH_PARTIES',
  borderCutting: '/api/parties?category=BORDER_CUTTING_PARTIES',
  handwork: '/api/parties?category=HANDWORK_PARTIES',
};

/** { token, loadedAt, data } for the last complete load, or null. */
let cached = null;
/** { token, promise } while a load is running (shared by concurrent callers). */
let inFlight = null;

function currentToken() {
  try {
    return localStorage.getItem('token') || '';
  } catch {
    return '';
  }
}

/** Returns the cached lists when they are fresh and belong to the current session, else null. */
export function readFreshOrderLookups() {
  const token = currentToken();
  if (!cached || cached.token !== token) return null;
  if (Date.now() - cached.loadedAt >= LOOKUP_CACHE_TTL_MS) return null;
  return cached.data;
}

async function fetchAllLookups(authFetch) {
  let complete = true;
  const keys = Object.keys(LOOKUP_REQUESTS);
  const results = await Promise.all(
    keys.map((key) =>
      authFetch(LOOKUP_REQUESTS[key]).catch((err) => {
        // 403 = this user may not see that list; an empty list is the correct answer.
        // Anything else (network, 5xx) may be temporary, so don't keep it in the cache.
        if (err?.status !== 403) complete = false;
        return [];
      })
    )
  );
  const data = {};
  keys.forEach((key, i) => {
    data[key] = Array.isArray(results[i]) ? results[i] : [];
  });
  return { data, complete };
}

/**
 * Loads the lists. Uses the cache unless `force` is true. Concurrent calls share one load.
 * @param {(path: string) => Promise<any>} authFetch
 * @returns {Promise<Record<string, any[]>>}
 */
export function loadOrderLookups(authFetch, { force = false } = {}) {
  const token = currentToken();
  if (!force) {
    const fresh = readFreshOrderLookups();
    if (fresh) return Promise.resolve(fresh);
  }
  if (inFlight && inFlight.token === token) {
    return inFlight.promise;
  }
  const promise = fetchAllLookups(authFetch)
    .then(({ data, complete }) => {
      if (complete && currentToken() === token) {
        cached = { token, loadedAt: Date.now(), data };
      } else if (cached && cached.token === token) {
        // Keep older complete data, but don't serve it as "fresh" any more.
        cached = null;
      }
      return data;
    })
    .finally(() => {
      if (inFlight && inFlight.promise === promise) inFlight = null;
    });
  inFlight = { token, promise };
  return promise;
}

/**
 * Keeps the cache in step with lists changed on the order page itself
 * (e.g. a party created with the quick "+ Add" button). Does not extend the cache lifetime.
 * @param {Record<string, any[]>} patch
 */
export function updateCachedOrderLookups(patch) {
  if (!cached || cached.token !== currentToken()) return;
  cached = { ...cached, data: { ...cached.data, ...patch } };
}
